import { useCallback, useEffect, useRef, useState } from "react";
import type { Card, CardStatus } from "@feed/shared";
import { ApiError, api } from "../api";
import { appendPage, prependNew, replaceCard, unseen } from "./merge";

const POLL_MS = 60_000;

interface Options {
  status: CardStatus | "all";
  type?: string;
  // Look for new cards every minute while the tab is visible (the main feed only).
  poll?: boolean;
}

export interface CardList {
  cards: Card[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  // Cards that arrived while the reader was reading. They wait behind a pill and
  // never reorder the list under their thumb.
  pending: Card[];
  loadMore: () => void;
  reload: () => void;
  showPending: () => void;
  // Applies a changed card in place (status moves), keeping the list's order.
  patch: (card: Card) => void;
}

export function useCardList({ status, type, poll = false }: Options): CardList {
  const [cards, setCards] = useState<Card[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Card[]>([]);
  const [version, setVersion] = useState(0);
  // Answers from an older filter must not land on a newer one.
  const generation = useRef(0);
  const cardsRef = useRef<Card[]>([]);
  cardsRef.current = cards;

  const fail = (caught: unknown) => setError(caught instanceof ApiError ? caught.code : "generic");

  useEffect(() => {
    const mine = ++generation.current;
    setLoading(true);
    setError(null);
    setPending([]);
    api.cards
      .list({ status, type })
      .then((page) => {
        if (generation.current !== mine) return;
        setCards(page.cards);
        setNext(page.next_cursor);
      })
      .catch((caught) => generation.current === mine && fail(caught))
      .finally(() => generation.current === mine && setLoading(false));
  }, [status, type, version]);

  const loadMore = useCallback(() => {
    if (!next || loadingMore) return;
    const mine = generation.current;
    setLoadingMore(true);
    api.cards
      .list({ status, type, before: next })
      .then((page) => {
        if (generation.current !== mine) return;
        setCards((shown) => appendPage(shown, page.cards));
        setNext(page.next_cursor);
      })
      .catch(fail)
      .finally(() => setLoadingMore(false));
  }, [next, loadingMore, status, type]);

  useEffect(() => {
    if (!poll) return;
    const check = () => {
      if (document.visibilityState !== "visible") return;
      const mine = generation.current;
      api.cards
        .list({ status, type })
        .then((page) => {
          if (generation.current !== mine) return;
          setPending(unseen(cardsRef.current, page.cards));
        })
        .catch(() => undefined);
    };
    const id = window.setInterval(check, POLL_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", check);
    };
  }, [poll, status, type]);

  const showPending = useCallback(() => {
    setCards((shown) => prependNew(shown, pending));
    setPending([]);
  }, [pending]);

  const patch = useCallback((card: Card) => setCards((shown) => replaceCard(shown, card)), []);
  const reload = useCallback(() => setVersion((v) => v + 1), []);

  return { cards, loading, loadingMore, hasMore: next !== null, error, pending, loadMore, reload, showPending, patch };
}
